import { useEffect, useState } from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export function CoachRoute({ children }: { children: React.ReactNode }) {
  const { user, role, loading, isCoachOrAdmin, isCoach } = useUserRole();
  // Onboarding gate: si el coach se auto-registró pero todavía no
  // llenó su perfil mínimo, lo mandamos a /coach/onboarding antes de
  // dejarlo entrar a cualquier ruta de coach.
  //
  // Los 2 coaches existentes (Joel, Marco) NO tienen row en
  // coach_profiles porque fueron creados manualmente antes de esta
  // feature. Para ellos, la ausencia de la fila NO debe gatear el
  // onboarding — se trata como already-onboarded (backwards compat).
  // Solo gatea si la fila EXISTE y onboarding_completed=false.
  const [coachOnboardingPending, setCoachOnboardingPending] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user || !isCoach) {
      setCoachOnboardingPending(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('coach_profiles')
        .select('onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle();
      // Sin fila → backwards compat → no gatea
      // Con fila + onboarding_completed=false → gatea
      setCoachOnboardingPending(!!(data && !data.onboarding_completed));
    })();
  }, [user, isCoach]);

  if (loading || coachOnboardingPending === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Coach con onboarding pendiente → mandarlo a llenarlo antes de
  // poder entrar al panel.
  if (coachOnboardingPending) {
    return <Navigate to="/coach/onboarding" replace />;
  }

  if (!isCoachOrAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Acceso Restringido
            </h2>
            <p className="text-muted-foreground">
              Esta sección es exclusiva para coaches y administradores. Tu rol actual es: <span className="font-medium">{role || 'cliente'}</span>
            </p>
          </div>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}